import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import AdminLayout from '../../components/layout/AdminLayout';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';

type AssistedRow = {
  id: string; estado: string; preapproval_id: string | null; checkout_opened_at: string | null;
  checkout_expires_at: string | null; created_at: string;
  usuario: { nombre: string; email: string } | null;
  plan: { nombre: string; precio: number } | null;
};

export default function InscripcionAsistenciaPage() {
  const { sucursalActiva } = useSucursal();
  const [rows, setRows] = useState<AssistedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [link, setLink] = useState('');
  const [qr, setQr] = useState('');
  const [selectedName, setSelectedName] = useState('');

  const invoke = useCallback(async (body: Record<string, unknown>) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('La sesión administrativa venció.');
    const { data, error: invokeError } = await supabase.functions.invoke('mercadopago-subscription-start', {
      headers: { Authorization: `Bearer ${session.access_token}` }, body: { ...body, session_token: session.access_token },
    });
    if (invokeError) {
      let detail = '';
      try { detail = String((await invokeError.context?.clone().json())?.error || ''); } catch { /* response was not JSON */ }
      throw new Error(detail || invokeError.message);
    }
    return data;
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!sucursalActiva?.gimnasio_id) return;
    if (!quiet) setLoading(true);
    try {
      const data = await invoke({ action: 'admin_list', gimnasio_id: sucursalActiva.gimnasio_id });
      setRows((data?.rows || []) as AssistedRow[]);
      setError('');
    } catch (cause) { if (!quiet) setError(cause instanceof Error ? cause.message : 'No se pudieron cargar las inscripciones.'); }
    finally { if (!quiet) setLoading(false); }
  }, [invoke, sucursalActiva?.gimnasio_id]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(true), 10_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const showLink = async (row: AssistedRow) => {
    try {
      const data = await invoke({ action: 'admin_link', gimnasio_id: sucursalActiva?.gimnasio_id, suscripcion_id: row.id });
      const url = String(data?.init_point || '');
      if (!url) throw new Error('No se pudo generar el enlace.');
      setSelectedName(row.usuario?.nombre || 'Cliente');
      setLink(url);
      setQr(await QRCode.toDataURL(url, { width: 720, margin: 2, errorCorrectionLevel: 'H', color: { dark: '#172111', light: '#ffffff' } }));
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'No se pudo recuperar la inscripción.'); }
  };
  const pairTablet = async () => {
    try {
      const data = await invoke({ action: 'admin_pair', gimnasio_id: sucursalActiva?.gimnasio_id, nombre: 'Tablet de asistencia Mowe' });
      const url = String(data?.pairing_url || '');
      if (!url) throw new Error('No se pudo generar la vinculación.');
      setSelectedName('Vincular tablet de asistencia');
      setLink(url);
      setQr(await QRCode.toDataURL(url, { width: 720, margin: 2, errorCorrectionLevel: 'H', color: { dark: '#172111', light: '#ffffff' } }));
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'No se pudo vincular la tablet.'); }
  };

  return <AdminLayout><main className="p-6 space-y-6">
    <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div><h1 className="text-3xl font-bold">Asistencia de inscripción</h1><p className="text-gray-500 mt-1">Recupera un pago, muestra su QR o ayuda al cliente desde esta tablet.</p></div>
      <div className="flex flex-wrap gap-2"><button onClick={() => void pairTablet()} className="px-4 py-3 bg-[#314a25] text-white rounded-xl font-semibold"><i className="ri-device-line mr-2"/>Vincular otra tablet</button><button onClick={() => void load()} className="px-4 py-3 border rounded-xl font-semibold"><i className="ri-refresh-line mr-2"/>Actualizar</button></div>
    </header>
    {error && <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">{error}</div>}
    <section className="bg-white border rounded-2xl overflow-hidden">
      {loading ? <div className="p-16 text-center"><i className="ri-loader-4-line animate-spin text-4xl"/></div> : rows.length === 0 ?
        <div className="p-16 text-center text-gray-500"><i className="ri-checkbox-circle-line text-5xl text-emerald-600"/><p className="mt-3">No hay pagos de Mercado Pago pendientes.</p></div> :
        <div className="divide-y">{rows.map(row => <article key={row.id} className="p-5 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1"><h2 className="font-bold text-lg">{row.usuario?.nombre || 'Cliente'}</h2><p className="text-sm text-gray-500">{row.usuario?.email}</p><p className="mt-2 text-sm"><b>{row.plan?.nombre || 'Plan'}</b>{row.plan ? ` · $${Number(row.plan.precio).toLocaleString('es-CL')}` : ''}</p></div>
          <div className="text-sm min-w-44"><span className={`px-3 py-1 rounded-full ${row.preapproval_id ? 'bg-blue-50 text-blue-700' : row.checkout_opened_at ? 'bg-violet-50 text-violet-700' : 'bg-amber-50 text-amber-700'}`}>{row.preapproval_id ? 'En Mercado Pago' : row.checkout_opened_at ? 'Abierta' : 'Esperando'}</span><p className="text-gray-400 mt-2">{new Date(row.created_at).toLocaleString('es-CL')}</p></div>
          <div className="flex flex-wrap gap-2"><button onClick={() => void showLink(row)} className="px-4 py-3 rounded-xl bg-[#314a25] text-white font-semibold"><i className="ri-qr-code-line mr-2"/>Mostrar QR</button></div>
        </article>)}</div>}
    </section>
    {link && <div className="fixed inset-0 z-[120] bg-black/60 p-4 grid place-items-center"><section className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-xl">
      <header className="p-5 border-b flex justify-between"><div><h2 className="text-xl font-bold">Continuar inscripción</h2><p className="text-sm text-gray-500">{selectedName}</p></div><button onClick={() => { setLink(''); setQr(''); }} className="text-2xl"><i className="ri-close-line"/></button></header>
      <div className="p-6 text-center"><div className="max-w-xs mx-auto border rounded-2xl p-3">{qr && <img src={qr} alt="QR para continuar" className="w-full"/>}</div><p className="text-sm text-gray-500 mt-4">Escanea el código en la tablet que deseas utilizar o abre el enlace directamente.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-5"><button onClick={() => void navigator.clipboard.writeText(link)} className="px-4 py-3 border rounded-xl font-semibold">Copiar enlace</button><button onClick={() => window.open(link, '_blank', 'noopener,noreferrer')} className="px-4 py-3 bg-[#314a25] text-white rounded-xl font-semibold">Continuar aquí</button></div>
      </div></section></div>}
  </main></AdminLayout>;
}
